// src/host/HostDashboard.jsx
import React from "react";
import { Link } from "react-router-dom";

const BRAND = "#7C9082";
const BRAND_DARK = "#6C7F72";
const BRAND_SOFT = "#E4EBE6";

export default function HostDashboard() {
  // TODO: replace with real data
  const stats = {
    upcomingBookings: 3,
    thisMonthEarnings: "$2,430",
    occupancyRate: "68%",
    avgDailyRate: "$215",
  };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-neutral-50">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 pb-24 pt-6 lg:pt-8">
        {/* Top: greeting + quick actions */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-neutral-500 shadow-sm ring-1 ring-neutral-200">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: BRAND }}
              />
              Host dashboard
            </div>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">
              Welcome back, host.
            </h1>
            <p className="mt-2 max-w-xl text-sm text-neutral-600">
              See how your space is performing, manage bookings, and keep your
              calendar up to date — all in one place.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Link
              to="/host/listings/new"
              className="inline-flex items-center justify-center rounded-full bg-neutral-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-neutral-800"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Create a new listing
            </Link>
            <Link
              to="/host/calendar"
              className="inline-flex items-center justify-center rounded-full border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-800 shadow-sm hover:border-neutral-400 hover:bg-neutral-50"
            >
              <CalendarIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Open calendar
            </Link>
          </div>
        </div>

        {/* Stats row */}
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Upcoming bookings"
            value={stats.upcomingBookings}
            hint="Next 30 days"
            icon={SuitcaseIcon}
          />
          <StatCard
            label="Earnings this month"
            value={stats.thisMonthEarnings}
            hint="Payouts before fees"
            icon={CurrencyIcon}
          />
          <StatCard
            label="Occupancy rate"
            value={stats.occupancyRate}
            hint="This month"
            icon={OccupancyIcon}
          />
          <StatCard
            label="Average daily rate"
            value={stats.avgDailyRate}
            hint="Booked dates"
            icon={PriceTagIcon}
          />
        </section>

        {/* Main content: bookings (no fake list) + tasks */}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          {/* Upcoming bookings – EMPTY STATE ONLY (wire your real data later) */}
          <section className="rounded-2xl border border-neutral-200 bg-white/80 p-4 shadow-sm sm:p-5">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-neutral-900">
                  Upcoming bookings
                </h2>
                <p className="mt-1 text-xs text-neutral-500">
                  When guests book, they’ll appear here with times and details.
                </p>
              </div>
              <Link
                to="/host/bookings"
                className="text-xs font-medium text-neutral-700 underline-offset-2 hover:underline"
              >
                View all
              </Link>
            </div>

            <EmptyState />
          </section>

          {/* Tasks + quick tip */}
          <section className="flex flex-col gap-4">
            <div className="rounded-2xl border border-neutral-200 bg-white/90 p-4 shadow-sm sm:p-5">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-neutral-900">
                    Suggested actions
                  </h2>
                  <p className="mt-1 text-xs text-neutral-500">
                    A few quick tweaks to help you get more bookings.
                  </p>
                </div>
              </div>

              <ul className="space-y-3">
                <TaskRow
                  label="Add more photos to your top listing"
                  description="Listings with 15+ photos get more views and bookings."
                  cta="Add photos"
                  to="/host/listings"
                />
                <TaskRow
                  label="Review your pricing"
                  description="Make sure your daytime and overnight rates reflect demand."
                  cta="Adjust pricing"
                  to="/host/listings"
                />
                <TaskRow
                  label="Update your calendar"
                  description="Block dates you can’t host to avoid awkward requests."
                  cta="Open calendar"
                  to="/host/calendar"
                />
              </ul>
            </div>

            <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/60 p-4 text-xs text-neutral-600">
              <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-700">
                <LightbulbIcon className="h-3 w-3" />
                Tip
              </div>
              <p className="mt-1">
                Listings that clearly explain{" "}
                <span className="font-medium">
                  what the space is perfect for
                </span>{" "}
                (showers, dinners, offsites, shoots) tend to get more
                high-intent guests.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Parts --------------------------------- */

function StatCard({ label, value, hint, icon: Icon }) {
  return (
    <div className="group flex flex-col justify-between rounded-2xl border border-neutral-200 bg-white/90 p-3 text-sm shadow-sm transition hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-neutral-500">
            {label}
          </p>
          <p className="mt-1 text-lg font-semibold text-neutral-900">
            {value}
          </p>
        </div>
        <div
          className="flex h-9 w-9 items-center justify-center rounded-full"
          style={{ backgroundColor: BRAND_SOFT, color: BRAND_DARK }}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {hint && (
        <p className="mt-1 text-[11px] text-neutral-500 group-hover:text-neutral-600">
          {hint}
        </p>
      )}
    </div>
  );
}

function TaskRow({ label, description, cta, to }) {
  return (
    <li className="flex gap-3 rounded-xl border border-neutral-200/70 bg-neutral-50/80 p-3 hover:border-neutral-300 hover:bg-white">
      <div
        className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full"
        style={{ backgroundColor: BRAND_SOFT, color: BRAND_DARK }}
      >
        <SparkleIcon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-neutral-900">{label}</p>
        <p className="mt-0.5 text-[11px] text-neutral-500">{description}</p>
        <Link
          to={to}
          className="mt-2 inline-flex items-center text-[11px] font-medium text-neutral-800 underline-offset-2 hover:underline"
        >
          {cta}
          <ArrowRightIcon className="ml-1 h-3 w-3" />
        </Link>
      </div>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-neutral-200 bg-neutral-50/70 px-4 py-8 text-center">
      <div
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-full"
        style={{ backgroundColor: BRAND_SOFT }}
      >
        <CalendarIcon className="h-5 w-5" style={{ color: BRAND_DARK }} />
      </div>
      <p className="text-sm font-medium text-neutral-900">
        No upcoming bookings yet
      </p>
      <p className="mt-1 max-w-xs text-xs text-neutral-500">
        When a guest books, you’ll see the date, time, and space details here.
      </p>
      <Link
        to="/host/listings"
        className="mt-3 inline-flex items-center rounded-full bg-neutral-900 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-neutral-800"
      >
        Go to your listings
      </Link>
    </div>
  );
}

/* --------------------------------- Icons --------------------------------- */

function PlusIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 4v12M4 10h12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CalendarIcon({ className = "", style }) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="4"
        width="14"
        height="13"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M3 8h14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M7 3v3M13 3v3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SuitcaseIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="6"
        width="14"
        height="11"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8 6V5.5A1.5 1.5 0 0 1 9.5 4h1A1.5 1.5 0 0 1 12 5.5V6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CurrencyIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M11.5 5.2c-.4-.2-.9-.4-1.5-.4C8.9 4.8 8 5.6 8 6.7c0 .9.6 1.5 1.6 1.8l1.1.3c1.2.3 1.9.9 1.9 2 0 1.2-1 2.1-2.5 2.1-.7 0-1.4-.2-2-.6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M10 3v14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function OccupancyIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="9"
        width="3.5"
        height="7"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect
        x="8.25"
        y="6"
        width="3.5"
        height="10"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect
        x="13.5"
        y="4"
        width="3.5"
        height="12"
        rx="1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function PriceTagIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10.6 3.3 4.4 9.5c-.3.3-.4.7-.4 1.1v3.8c0 .9.7 1.6 1.6 1.6h3.8c.4 0 .8-.2 1.1-.4l6.2-6.2c.3-.3.5-.8.5-1.2V5c0-.9-.7-1.6-1.6-1.6h-2.9c-.4 0-.9.2-1.1.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="13.4" cy="7" r="0.9" fill="currentColor" />
    </svg>
  );
}

function SparkleIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 3.5 10.8 6c.3.8.7 1.2 1.5 1.5L15 8.4l-2.7.9c-.8.3-1.2.7-1.5 1.5L10 13l-.8-2.2c-.3-.8-.7-1.2-1.5-1.5L5 8.4l2.7-.9c.8-.3 1.2-.7 1.5-1.5L10 3.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRightIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 8h8M8.5 4.5 12 8l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LightbulbIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 3a4.5 4.5 0 0 1 3.3 7.6c-.6.6-1 1.4-1.1 2.2H7.8c-.1-.8-.5-1.6-1.1-2.2A4.5 4.5 0 0 1 10 3Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8.5 16.2h3M9 18h2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
